/**
 * @file Hybrid validation utilities combining validator.js library with custom validators.
 *
 * Uses validator.js for standard validations (email, IP, FQDN, port, CIDR).
 * Custom validators for domain-specific needs (DNS labels, TLDs, org names, comments).
 *
 * All exports maintain backwards-compatible API - callers don't need to change.
 *
 * @todo Convert to TypeScript. Simple conversion - all functions are (string) → boolean.
 *       Tests are already in place (validationUtils.spec.ts) to ensure safe migration.
 */

// validator.js library imports (tree-shaken)
import libIsEmail from 'validator/es/lib/isEmail'
import libIsFQDN from 'validator/es/lib/isFQDN'
import libIsIP from 'validator/es/lib/isIP'
import libIsIPRange from 'validator/es/lib/isIPRange'
import libIsPort from 'validator/es/lib/isPort'

// =============================================================================
// DNS / Host Validators
// =============================================================================

/**
 * Returns true if value is a syntactically valid DNS label (RFC-ish).
 * - 1..63 chars
 * - alnum at ends
 * - hyphens allowed in middle
 * - optional punycode prefix allowed
 *
 * @param {string} label
 * @returns {boolean}
 */
export const isValidDnsLabel = (label) => {
    const v = (label ?? '').toString().trim().toLowerCase()
    if (!v) return false
    if (v.length > 63) return false
    return /^(?:xn--)?[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(v)
}

/**
 * Returns true if value is a syntactically valid FQDN hostname.
 * Uses validator.js library with sensible defaults.
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isValidFqdn = (value) => {
    const v = (value ?? '').toString().trim()
    if (!v) return false
    return libIsFQDN(v, { allow_trailing_dot: true, allow_numeric_tld: false })
}

/**
 * Backwards-compatible domain validator used across the app.
 * Defined as syntactic domain/FQDN validity (multi-label).
 *
 * NOTE: This does NOT check if something is a "registrable domain"
 * (requires Public Suffix List, enforced backend if needed).
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isValidDomain = (value) => {
    return isValidFqdn(value)
}

/**
 * Validates an email address.
 * Uses validator.js library. Trims surrounding whitespace and supports
 * + sign for email filtering.
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isValidEmail = (value) => {
    const v = (value ?? '').toString().trim()
    if (!v) return false
    return libIsEmail(v, { allow_utf8_local_part: false })
}
/**
 * Validates an IPv4 address.
 * Uses validator.js library.
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isValidIp = (value) => {
    const v = (value ?? '').toString().trim()
    if (!v) return false
    return libIsIP(v, 4)
}

/**
 * Validates an IPv4 address with CIDR notation.
 * Uses validator.js library.
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isValidIpWithCidr = (value) => {
    const v = (value ?? '').toString().trim()
    if (!v) return false
    return libIsIPRange(v, 4)
}

/**
 * Validates an IPv6 address.
 * Uses validator.js library. Accepts compressed forms (e.g., 2001:db8::1, ::1).
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isValidIpv6 = (value) => {
    const v = (value ?? '').toString().trim()
    if (!v) return false
    // Strip brackets if provided (e.g., "[2001:db8::1]")
    const cleaned = v.replace(/^\[/, '').replace(/\]$/, '')
    return libIsIP(cleaned, 6)
}

/**
 * Validates an IPv6 address with CIDR notation.
 * Uses validator.js library.
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isValidIpv6WithCidr = (value) => {
    const v = (value ?? '').toString().trim()
    if (!v) return false
    return libIsIPRange(v, 6)
}

/**
 * Validates a port number (1-65535).
 * Uses validator.js library.
 *
 * @param {string|number} value
 * @returns {boolean}
 */
export const isValidPort = (value) => {
    const v = String(value ?? '').trim()
    if (!v) return false
    return libIsPort(v)
}
/**
 * Returns true if value is a valid IPv4 or IPv6 address.
 * CIDR notation not supported here — use separate functions if needed.
 */
export const isValidIpOrIpv6 = (value) => {
    return isValidIp(value) || isValidIpv6(value)
}
export const isValidIpOrIpv6WithCidr = (value) => {
    return isValidIpWithCidr(value) || isValidIpv6WithCidr(value)
}

/**
 * Validates TLD input.
 * Accepts "com", ".com", "xn--p1ai", etc., but disallows "co.m" or "com."
 * Allows Punycode TLDs (e.g., "xn--") and strips optional leading dot.
 *
 * @param {string} tld
 * @returns {boolean}
 */
export const isValidTld = (tld) => {
    if (!tld) return false

    const cleaned = tld.trim().toLowerCase().replace(/^\./, '')

    // Match basic a-z TLDs and Punycode TLDs (xn-- format)
    return /^([a-z]{2,63}|xn--[a-z0-9-]{2,59})$/.test(cleaned)
}

/**
 * Validates a comment field.
 * Allows letters, numbers, spaces, and: . , - & '
 *
 * @param {string} comment
 * @returns {boolean}
 */
export const isValidComment = (comment) => {
    const v = (comment ?? '').toString().trim()
    if (!v) return false

    const commentRegex = /^[a-zA-Z0-9\s.,\-&']+$/
    return commentRegex.test(v)
}

/**
 * Organization name validation regex.
 * Allows letters, numbers, spaces, and: . , - & '
 * @type {RegExp}
 */
export const ORG_NAME_REGEX = /^[\p{L}\p{N} .,&'-]{1,100}$/u

/**
 * Validates an organization name.
 * @param {string} name - The organization name to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidOrgName = (name) => {
    return ORG_NAME_REGEX.test(name.trim())
}

/**
 * Returns true if value is a syntactically valid subdomain hostname.
 * This is only meaningful relative to a base domain, e.g.:
 * - isValidSubdomainOf("test.cirrus.ci", "cirrus.ci") -> true
 *
 * @param {string} host
 * @param {string} baseDomain
 * @returns {boolean}
 */
export const isValidSubdomainOf = (host, baseDomain) => {
    if (!isValidFqdn(host) || !isValidFqdn(baseDomain)) return false
    const h = host.toLowerCase().replace(/\.$/, '')
    const b = baseDomain.toLowerCase().replace(/\.$/, '')
    return h === b ? false : h.endsWith(`.${b}`)
}

/**
 * Relay host:
 * - valid IPv4 or IPv6 OR valid FQDN hostname
 * - Optional: allow single-label internal hostnames if we want.
 *
 * @param {string} value
 * @param {{ allowSingleLabel?: boolean }} [opts]
 * @returns {boolean}
 */
export const isValidRelayHost = (value, opts = {}) => {
    const v = (value ?? '').toString().trim()
    if (!v) return false

    if (isValidIp(v) || isValidIpv6(v)) return true
    if (isValidFqdn(v)) return true

    if (opts.allowSingleLabel) {
        // Single-label internal hostname (e.g. "mailserver")
        return isValidDnsLabel(v)
    }

    return false
}
